// Verdant Wilds content (Bible VW-01..VW-25). Bible-specified where stated; TEST
// VALUES otherwise. Core HP scaled by the owner's ~0.53 TTK factor (7,200 → 3,800).

import type {
  AbilityDef,
  BossExtras,
  BuildingDef,
  CardDef,
  RitualDef,
  SpellDef,
  TrapDef,
  UnitDef,
} from './types';

// ---------------------------------------------------------------- units

export const VW_UNITS: UnitDef[] = [
  {
    id: 'u-vw-worker',
    name: 'Grove Tender',
    faction: 'verdant-wilds',
    sizeTag: 'small',
    roleTags: ['worker'],
    squadMemberCount: 1,
    maxHealth: 48, // baseline worker — numerous but individually less durable
    armor: 'none',
    moveSpeed: 3.4,
    targetPriority: [],
    abilities: [],
    command: 0,
    captureWeight: 0,
    ui: { color: 0x9dc06a, scale: 0.55, label: 'Tender' },
  },
  {
    id: 'u-vw-thorn-guard',
    name: 'Thorn Guard',
    faction: 'verdant-wilds',
    sizeTag: 'small',
    roleTags: ['frontline'],
    squadMemberCount: 6, // bible VW-09
    maxHealth: 760, // TEST
    armor: 'heavy', // Briar Skin reflect folded into heavier armor (deviation)
    moveSpeed: 3.0,
    attack: { damage: 28, period: 1.0, range: 1.6 },
    targetPriority: ['squad', 'worker', 'building'],
    abilities: ['ab-vw-rooted-defense'],
    command: 3,
    captureWeight: 1,
    ui: { color: 0x5f9e58, scale: 0.9, label: 'Thorn Guard' },
  },
  {
    id: 'u-vw-vine-archers',
    name: 'Vine Archers',
    faction: 'verdant-wilds',
    sizeTag: 'small',
    roleTags: ['ranged'],
    squadMemberCount: 5, // bible VW-10
    maxHealth: 540, // TEST
    armor: 'light',
    moveSpeed: 3.2,
    attack: { damage: 38, period: 1.4, range: 11, projectileSpeed: 26 },
    targetPriority: ['squad', 'worker', 'large'],
    abilities: ['ab-vw-snaring-volley'],
    command: 3,
    captureWeight: 1,
    ui: { color: 0x7eb06a, scale: 0.85, label: 'Vine Archers' },
  },
  {
    id: 'u-vw-root-mender',
    name: 'Root Mender',
    faction: 'verdant-wilds',
    sizeTag: 'small',
    roleTags: ['support'],
    squadMemberCount: 3, // bible VW-11
    maxHealth: 420, // TEST
    armor: 'light',
    moveSpeed: 3.1,
    attack: { damage: 14, period: 1.4, range: 7, projectileSpeed: 20 },
    targetPriority: ['squad'],
    abilities: ['ab-vw-mend-the-wounded'],
    command: 4,
    captureWeight: 0.5,
    ui: { color: 0xa8d08a, scale: 0.8, label: 'Mender' },
  },
  {
    id: 'u-vw-razorhorn',
    name: 'Razorhorn Pack',
    faction: 'verdant-wilds',
    sizeTag: 'small',
    roleTags: ['raider', 'frontline'],
    squadMemberCount: 4, // bible VW-12
    maxHealth: 680, // TEST
    armor: 'moderate',
    moveSpeed: 4.0,
    attack: { damage: 44, period: 1.1, range: 1.6 },
    targetPriority: ['worker', 'squad', 'siege', 'trap'],
    abilities: ['ab-vw-splinter-charge'],
    command: 4,
    captureWeight: 1,
    ui: { color: 0x8a6e4a, scale: 0.95, label: 'Razorhorns' },
  },
  {
    id: 'u-vw-canopy-stalkers',
    name: 'Canopy Stalkers',
    faction: 'verdant-wilds',
    sizeTag: 'small',
    roleTags: ['ranged', 'raider'],
    squadMemberCount: 5, // bible VW-13
    maxHealth: 500, // TEST
    armor: 'light',
    moveSpeed: 3.8,
    attack: { damage: 34, period: 1.2, range: 9, projectileSpeed: 28 },
    targetPriority: ['worker', 'squad', 'siege'],
    abilities: ['ab-vw-quill-retreat'],
    command: 4,
    captureWeight: 0.5,
    ui: { color: 0x6a8a5a, scale: 0.85, label: 'Stalkers' },
  },
  {
    id: 'u-vw-groveback',
    name: 'Groveback Beast',
    faction: 'verdant-wilds',
    sizeTag: 'large',
    roleTags: ['frontline'],
    squadMemberCount: 1,
    maxHealth: 1900, // TEST (bible VW-14 Large anchor)
    armor: 'heavy',
    moveSpeed: 2.3,
    attack: { damage: 70, period: 1.6, range: 2.2 },
    targetPriority: ['squad', 'building'],
    abilities: ['ab-vw-grounding-stomp'],
    command: 7,
    captureWeight: 0.5,
    mountSlots: 1, // bible VW-14: living platform for Canopy Riders
    ui: { color: 0x6f8a52, scale: 1.5, label: 'Groveback' },
  },
  {
    // Visual VW-11 Barkwright Circle mapped onto the existing specialist-worker mechanics;
    // the production rig contains four druids, while the sim intentionally keeps one WorkerEnt
    // (documented in README Deviations).
    id: 'u-vw-barkwright',
    name: 'Barkwright Circle',
    faction: 'verdant-wilds',
    sizeTag: 'small',
    roleTags: ['worker', 'builder'],
    squadMemberCount: 1,
    maxHealth: 82, // bible VW-15: +70% over the Verdant worker
    armor: 'light',
    moveSpeed: 3.2,
    targetPriority: [],
    abilities: [],
    command: 0,
    captureWeight: 0,
    workerVariant: { buildMult: 1.5, repairMult: 1.3, carryMult: 1.15 },
    ui: { color: 0xb8a05f, scale: 0.85, label: 'Barkwrights' },
  },
  {
    id: 'u-vw-canopy-riders',
    name: 'Canopy Riders',
    faction: 'verdant-wilds',
    sizeTag: 'medium',
    roleTags: ['ranged', 'support'],
    squadMemberCount: 4, // bible VW-16
    maxHealth: 520, // TEST
    armor: 'light',
    moveSpeed: 3.4,
    attack: {
      damage: 42,
      period: 1.5,
      range: 12,
      projectileSpeed: 28,
      damageVs: { large: 1.3, giant: 1.5 }, // Elevated Volley // TEST — giant-killer bump
    },
    targetPriority: ['giant', 'large', 'squad', 'siege'],
    abilities: ['ab-cavalry-charge'],
    command: 5,
    captureWeight: 0.5,
    mountableOn: ['u-vw-groveback', 'boss-vw-ancient-groveback', 'boss-vw-worldroot-titan'],
    ui: { color: 0x8ab86a, scale: 0.85, label: 'Riders' },
  },
  {
    id: 'u-vw-sproutling-swarm',
    name: 'Sproutling Swarm',
    faction: 'verdant-wilds',
    sizeTag: 'small',
    roleTags: ['frontline'],
    squadMemberCount: 8, // bible v0.2.4 VW-04
    maxHealth: 360, // TEST
    armor: 'none',
    moveSpeed: 3.4,
    attack: { damage: 10, period: 1.0, range: 1.2 }, // TEST
    targetPriority: ['squad', 'worker'],
    abilities: ['ab-vw-bud-and-split', 'ab-vw-pollen-pop'],
    command: 2,
    captureWeight: 1,
    ui: { color: 0xc4de8f, scale: 0.65, label: 'Sproutlings' },
  },
  {
    id: 'u-vw-sporecallers',
    name: 'Sporecallers',
    faction: 'verdant-wilds',
    sizeTag: 'small',
    roleTags: ['support', 'ranged'],
    squadMemberCount: 3, // bible v0.2.4 VW-07
    maxHealth: 430, // TEST
    armor: 'light',
    moveSpeed: 3.0,
    attack: { damage: 22, period: 1.6, range: 9, projectileSpeed: 18 }, // TEST
    targetPriority: ['squad', 'worker'],
    abilities: ['ab-vw-spore-cloud', 'ab-vw-fungal-renewal'],
    command: 4,
    captureWeight: 0.5,
    maxActiveUnits: 2, // bible v0.2.4 VW-07: maximum two active
    ui: { color: 0x9d86b8, scale: 0.85, label: 'Sporecallers' },
  },
  {
    id: 'u-vw-briarback-wardens',
    name: 'Briarback Wardens',
    faction: 'verdant-wilds',
    sizeTag: 'small',
    roleTags: ['frontline'],
    squadMemberCount: 5, // bible v0.2.4 VW-08
    maxHealth: 900, // TEST
    armor: 'very-heavy',
    moveSpeed: 2.7,
    attack: { damage: 30, period: 1.3, range: 1.6 }, // TEST
    targetPriority: ['squad', 'large', 'building'],
    abilities: ['ab-vw-protective-bramble', 'ab-vw-thornburst'],
    command: 5,
    captureWeight: 1,
    ui: { color: 0x7a5c38, scale: 0.95, label: 'Wardens' },
  },
  {
    id: 'u-vw-moonfang-hunters',
    name: 'Moonfang Hunters',
    faction: 'verdant-wilds',
    sizeTag: 'small',
    roleTags: ['raider'],
    squadMemberCount: 4, // bible v0.2.4 VW-12
    maxHealth: 640, // TEST
    armor: 'moderate',
    moveSpeed: 4.2, // Mark Prey speed bonus folded into fast base speed (deviation)
    attack: { damage: 48, period: 1.1, range: 1.6 }, // TEST
    targetPriority: ['worker', 'squad', 'siege'],
    abilities: ['ab-vw-mark-prey', 'ab-vw-pack-shadow'],
    command: 5,
    captureWeight: 1,
    ui: { color: 0xb9c8cf, scale: 0.9, label: 'Moonfangs' },
  },
  {
    // no flight mechanic in the sim — fast skimming ground squad (Canopy Riders precedent)
    id: 'u-vw-skybloom-drakes',
    name: 'Skybloom Drakes',
    faction: 'verdant-wilds',
    sizeTag: 'large',
    roleTags: ['ranged', 'raider'],
    squadMemberCount: 3, // bible v0.2.4 VW-13
    maxHealth: 980, // TEST
    armor: 'moderate',
    moveSpeed: 4.4, // TEST — flying approximated as speed
    attack: { damage: 55, period: 1.5, range: 8, projectileSpeed: 20 }, // TEST nectar breath
    targetPriority: ['squad', 'worker', 'siege'],
    abilities: ['ab-vw-bloomfire', 'ab-vw-nectar-shield'],
    command: 6,
    captureWeight: 0.5,
    ui: { color: 0xd08fae, scale: 1.3, label: 'Drakes' },
  },
  {
    id: 'u-vw-ancient-treants',
    name: 'Ancient Treants',
    faction: 'verdant-wilds',
    sizeTag: 'large',
    roleTags: ['frontline'],
    squadMemberCount: 2, // bible v0.2.4 VW-14: construct pair
    maxHealth: 1700, // TEST
    armor: 'very-heavy',
    moveSpeed: 1.9, // painfully slow off territory
    attack: { damage: 85, period: 1.8, range: 2.4 }, // TEST
    targetPriority: ['squad', 'building'],
    abilities: ['ab-vw-rootwalk', 'ab-vw-branch-slam'],
    command: 6,
    captureWeight: 0.5,
    ui: { color: 0x5c4a33, scale: 1.6, label: 'Treants' },
  },
  {
    id: 'u-vw-thornmaw-hydra',
    name: 'Thornmaw Hydra',
    faction: 'verdant-wilds',
    sizeTag: 'large',
    roleTags: ['frontline', 'anti-large'],
    squadMemberCount: 1, // one multi-headed beast
    maxHealth: 2100, // TEST
    armor: 'heavy',
    moveSpeed: 2.6,
    attack: { damage: 60, period: 0.9, range: 2.2, damageVs: { small: 1.3 } }, // TEST — multi-head bites, anti-swarm
    targetPriority: ['squad', 'large', 'building'],
    abilities: ['ab-vw-regrow-heads', 'ab-vw-acid-sap'],
    command: 8,
    captureWeight: 0.5,
    ui: { color: 0x94b83c, scale: 1.55, label: 'Hydra' },
  },
  {
    // no flight mechanic in the sim — fast skimming support squad (deviation)
    id: 'u-vw-sunpetal-seraphs',
    name: 'Sunpetal Seraphs',
    faction: 'verdant-wilds',
    sizeTag: 'large',
    roleTags: ['support'],
    squadMemberCount: 3, // bible v0.2.4 VW-16
    maxHealth: 880, // TEST
    armor: 'light',
    moveSpeed: 4.0, // TEST — flying approximated as speed
    attack: { damage: 30, period: 1.4, range: 8, projectileSpeed: 22 }, // TEST
    targetPriority: ['squad'],
    abilities: ['ab-vw-solar-bloom', 'ab-vw-resurrection-seed'],
    command: 7,
    captureWeight: 0.5,
    ui: { color: 0xe8c95a, scale: 1.25, label: 'Seraphs' },
  },
  {
    id: 'u-vw-verdant-behemoth',
    name: 'Verdant Behemoth',
    faction: 'verdant-wilds',
    sizeTag: 'giant',
    roleTags: ['frontline', 'siege'],
    squadMemberCount: 1, // bible v0.2.4 VW-17
    maxHealth: 3900, // TEST — below the Worldroot Titan; giant HP pass: −25%
    armor: 'heavy',
    moveSpeed: 2.0,
    attack: { damage: 120, period: 1.9, range: 3.0, structureMult: 1.5 }, // TEST
    targetPriority: ['building', 'squad'],
    abilities: ['ab-vw-overgrowth-trail', 'ab-vw-crushing-roll', 'ab-vw-behemoth-charge'],
    command: 9,
    captureWeight: 0.5,
    ui: { color: 0x54702e, scale: 2.3, label: 'Behemoth' },
  },
  {
    id: 'boss-vw-thornback',
    name: 'Thornback Charger',
    faction: 'verdant-wilds',
    sizeTag: 'large',
    roleTags: ['boss', 'raider'],
    squadMemberCount: 1,
    maxHealth: 875, // halved from previous prototype value per owner feedback
    armor: 'heavy',
    moveSpeed: 3.4,
    attack: { damage: 90, period: 1.4, range: 2.2, structureMult: 1.4 },
    targetPriority: ['building', 'squad'],
    abilities: ['ab-vw-thornquake', 'ab-vw-thornback-charge'],
    command: 7,
    isBoss: true,
    captureWeight: 0.5,
    ui: { color: 0x7a9a4a, scale: 1.7, label: 'Thornback' },
  },
  {
    id: 'boss-vw-ancient-groveback',
    name: 'Ancient Groveback',
    faction: 'verdant-wilds',
    sizeTag: 'large',
    roleTags: ['boss', 'frontline'],
    squadMemberCount: 1,
    maxHealth: 2050, // halved from previous prototype value per owner feedback
    armor: 'very-heavy',
    moveSpeed: 2.0,
    attack: { damage: 75, period: 1.7, range: 2.5 },
    targetPriority: ['squad', 'building'],
    abilities: ['ab-vw-orchard-of-war'],
    command: 12,
    isBoss: true,
    captureWeight: 0.5,
    mountSlots: 1, // bible VW-24: one Canopy Rider squad
    ui: { color: 0x5a7a44, scale: 2.0, label: 'Ancient' },
  },
  {
    id: 'boss-vw-worldroot-titan',
    name: 'Worldroot Titan',
    faction: 'verdant-wilds',
    sizeTag: 'giant',
    roleTags: ['boss', 'siege'],
    squadMemberCount: 1,
    maxHealth: 2850, // halved from previous prototype value per owner feedback
    armor: 'heavy',
    moveSpeed: 1.7,
    attack: { damage: 150, period: 2.1, range: 3.5, structureMult: 1.8 },
    targetPriority: ['building', 'squad'],
    abilities: ['ab-vw-rootbreaker', 'ab-vw-walking-forest', 'ab-vw-titan-crush'],
    command: 20,
    isBoss: true,
    captureWeight: 0.5,
    mountSlots: 2, // bible VW-25: two attachments
    ui: { color: 0x4f7a3a, scale: 2.6, label: 'Titan' },
  },
  {
    // Core Annex System (owner spec 2026-07-13): grown by the Rootward Nursery, not trained.
    // Slow defensive treant; E: Take Root turns it into a stationary economic guardian.
    id: 'u-vw-rootwarden',
    name: 'Rootwarden Treant',
    faction: 'verdant-wilds',
    sizeTag: 'large',
    roleTags: ['frontline', 'support'],
    squadMemberCount: 1,
    maxHealth: 1450, // TEST
    armor: 'moderate',
    moveSpeed: 1.9, // TEST — deliberately slow
    // thorn volley only fires while rooted (spec: "gains … ranged thorn attacks")
    attack: { damage: 42, period: 1.4, range: 11, projectileSpeed: 22 }, // TEST
    attackRequiresRoot: true,
    targetPriority: ['squad', 'worker', 'large'],
    abilities: [],
    command: 2, // spec: permanent spawned units count against Command
    captureWeight: 0, // guardian, not a capture piece
    rootable: {
      uprootSeconds: 8, // spec: uprooting takes eight seconds
      auraSuppressedSeconds: 20, // spec: economy aura disabled for 20 s after uprooting
      damageTakenMult: 0.7, // spec "gains armor" approximated as −30% damage taken // TEST
      auraRadius: 12, // TEST
      workerHealPerSecond: 2, // spec: heals nearby workers slowly // TEST
      gatherMult: 1.15, // spec: +15% gathering speed
      retreatSpeedMult: 1.25, // spec: +25% retreat speed
    },
    ui: { color: 0x6a8f4e, scale: 1.35, label: 'Rootwarden' },
  },
  {
    // Core Annex System: grown by the Seedcaravan Bower. Slow noncombat seed-pod
    // creature; E: Plant Outpost grows a free Bramblehold where it stands.
    id: 'u-vw-seedback',
    name: 'Seedback Carrier',
    faction: 'verdant-wilds',
    sizeTag: 'large',
    roleTags: ['support'],
    squadMemberCount: 1,
    maxHealth: 900, // TEST
    armor: 'moderate',
    moveSpeed: 1.6, // TEST — a walking seed pod
    targetPriority: [],
    abilities: [],
    command: 0, // deployable building-carrier, not an army unit
    captureWeight: 0,
    deploysInto: {
      buildingId: 'b-vw-bramblehold',
      assembleSeconds: 50, // spec: after 50 s the seed grows into the outpost
      maxDeployed: 1, // spec: only one Bramblehold from this annex
      witherSeconds: 15, // spec: planting another withers the older one over 15 s
      label: 'Plant Outpost',
    },
    ui: { color: 0x8a9a52, scale: 1.4, label: 'Seedback' },
  },
];

// ---------------------------------------------------------------- abilities

export const VW_ABILITIES: AbilityDef[] = [
  {
    // bible VW-09 Rooted Defense (Briar Skin reflect folded into armor — deviation)
    id: 'ab-vw-rooted-defense',
    name: 'Rooted Defense',
    trigger: { when: 'chargedBy', sizeTags: ['medium', 'large', 'giant'] },
    internalCooldown: 15,
    telegraphSeconds: 0.3,
    targetSelector: 'self',
    effects: [{ fx: 'statMod', stat: 'damageTaken', mult: 0.85, duration: 6 }],
    ui: { label: 'Rooted Defense', showFloatingLabel: true },
  },
  {
    // bible VW-10 Snaring Volley (applies to the nearest approaching squad — deviation
    // from the multi-target seed spread)
    id: 'ab-vw-snaring-volley',
    name: 'Snaring Volley',
    trigger: { when: 'surroundedBy', count: 3, range: 9, sizeTags: ['small', 'medium'] },
    internalCooldown: 18,
    telegraphSeconds: 0.4,
    targetSelector: 'currentTarget',
    effects: [{ fx: 'slow', magnitude: 0.25, duration: 5 }],
    ui: { label: 'Snaring Volley', showFloatingLabel: true },
  },
  {
    // bible VW-11 Mend the Wounded
    id: 'ab-vw-mend-the-wounded',
    name: 'Mend the Wounded',
    trigger: { when: 'timer', period: 4 },
    internalCooldown: 4,
    telegraphSeconds: 0.3,
    targetSelector: 'self',
    effects: [{ fx: 'healLowestAlly', amount: 65, range: 10 }],
    ui: { label: 'Mend', showFloatingLabel: false },
  },
  {
    // bible VW-12 Splinter Charge
    id: 'ab-vw-splinter-charge',
    name: 'Splinter Charge',
    trigger: { when: 'enemyInRange', range: 12, minRange: 2.5 },
    internalCooldown: 17,
    telegraphSeconds: 0.4,
    targetSelector: 'triggeringEntity',
    effects: [{
      fx: 'charge',
      speedMult: 3.3,
      durationSeconds: 1.6,
      impactDamage: 90,
      impactKnockback: 2.0,
      impactKnockdownSeconds: 1.2,
    }],
    ui: { label: 'Splinter Charge', showFloatingLabel: true },
  },
  {
    // bible VW-13 Quill Retreat
    id: 'ab-vw-quill-retreat',
    name: 'Quill Retreat',
    trigger: { when: 'selfBelowHealth', fraction: 0.3, oncePerLife: true },
    internalCooldown: 30,
    telegraphSeconds: 0.2,
    targetSelector: 'self',
    effects: [{ fx: 'teleport', distance: 6, direction: 'away-from-target' }],
    ui: { label: 'Quill Retreat', showFloatingLabel: true },
  },
  {
    // bible VW-14 Grounding Stomp
    id: 'ab-vw-grounding-stomp',
    name: 'Grounding Stomp',
    trigger: { when: 'chargedBy', sizeTags: ['large', 'giant'] },
    internalCooldown: 22,
    telegraphSeconds: 0.4,
    targetSelector: 'triggeringEntity',
    effects: [{ fx: 'slow', magnitude: 0.3, duration: 6 }],
    ui: { label: 'Grounding Stomp', showFloatingLabel: true },
  },
  {
    // bible VW-23 Thornquake Charge
    id: 'ab-vw-thornquake',
    name: 'Thornquake Charge',
    trigger: { when: 'attackingTargetTag', tag: 'building' },
    internalCooldown: 19,
    telegraphSeconds: 0.5,
    targetSelector: 'self',
    effects: [{ fx: 'areaDamage', amount: 120, radius: 3.5 }],
    ui: { label: 'Thornquake', showFloatingLabel: true },
  },
  {
    id: 'ab-vw-thornback-charge',
    name: 'Thornback Charge',
    trigger: { when: 'enemyInRange', range: 12 },
    internalCooldown: 10,
    telegraphSeconds: 0.35,
    targetSelector: 'triggeringEntity',
    effects: [{
      fx: 'charge',
      speedMult: 3.2,
      durationSeconds: 2.4,
      impactDamage: 145,
      impactKnockback: 2.4,
      impactKnockdownSeconds: 3.2,
    }],
    ui: { label: 'Thornback Charge', showFloatingLabel: true },
  },
  {
    // bible VW-24 Orchard of War (Become the Grove's rooted mode simplified out — deviation)
    id: 'ab-vw-orchard-of-war',
    name: 'Orchard of War',
    trigger: { when: 'timer', period: 20 },
    internalCooldown: 20,
    telegraphSeconds: 0.5,
    targetSelector: 'self',
    effects: [{ fx: 'healLowestAlly', amount: 220, range: 12 }],
    ui: { label: 'Orchard of War', showFloatingLabel: true },
  },
  {
    // bible VW-25 Rootbreaker Fists (armor shred approximated as heavy bonus damage)
    id: 'ab-vw-rootbreaker',
    name: 'Rootbreaker Fists',
    trigger: { when: 'nthAttackVsStructure', n: 4 },
    internalCooldown: 2,
    telegraphSeconds: 0.5,
    targetSelector: 'currentTarget',
    effects: [{ fx: 'structureStrike', damage: 480, repairHalfSeconds: 3 }],
    ui: { label: 'Rootbreaker', showFloatingLabel: true },
  },
  {
    // bible VW-25 Walking Forest
    id: 'ab-vw-walking-forest',
    name: 'Walking Forest',
    trigger: { when: 'timer', period: 18 },
    internalCooldown: 18,
    telegraphSeconds: 0.5,
    targetSelector: 'self',
    effects: [{ fx: 'plantSapling', buildingId: 'b-vw-sapling', duration: 20 }],
    ui: { label: 'Walking Forest', showFloatingLabel: true },
  },
  {
    // bible v0.2.4 VW-04 Bud and Split (structure-proximity regrow approximated as a
    // periodic self-heal — deviation)
    id: 'ab-vw-bud-and-split',
    name: 'Bud and Split',
    trigger: { when: 'timer', period: 20 },
    internalCooldown: 20,
    telegraphSeconds: 0.3,
    targetSelector: 'self',
    effects: [{ fx: 'heal', amount: 45, overSeconds: 4 }], // TEST
    ui: { label: 'Bud and Split', showFloatingLabel: false },
  },
  {
    // bible v0.2.4 VW-04 Pollen Pop (enemy accuracy debuff approximated as self
    // damage-taken reduction — deviation)
    id: 'ab-vw-pollen-pop',
    name: 'Pollen Pop',
    trigger: { when: 'selfBelowHealth', fraction: 0.5, oncePerLife: true },
    internalCooldown: 30,
    telegraphSeconds: 0.2,
    targetSelector: 'self',
    effects: [{ fx: 'statMod', stat: 'damageTaken', mult: 0.75, duration: 5 }], // TEST
    ui: { label: 'Pollen Pop', showFloatingLabel: true },
  },
  {
    // bible v0.2.4 VW-07 Spore Cloud (persistent cloud approximated as a slow +
    // damage debuff on the clustered attacker — deviation)
    id: 'ab-vw-spore-cloud',
    name: 'Spore Cloud',
    trigger: { when: 'surroundedBy', count: 2, range: 8 },
    internalCooldown: 16,
    telegraphSeconds: 0.5,
    targetSelector: 'triggeringEntity',
    effects: [
      { fx: 'slow', magnitude: 0.3, duration: 4 }, // TEST
      { fx: 'statMod', stat: 'damage', mult: 0.8, duration: 4 }, // TEST — accuracy loss as damage loss
    ],
    ui: { label: 'Spore Cloud', showFloatingLabel: true },
  },
  {
    // bible v0.2.4 VW-07 Fungal Renewal (cloud-conversion healing patch approximated
    // as periodic lowest-ally healing — deviation)
    id: 'ab-vw-fungal-renewal',
    name: 'Fungal Renewal',
    trigger: { when: 'timer', period: 6 },
    internalCooldown: 6,
    telegraphSeconds: 0.3,
    targetSelector: 'self',
    effects: [{ fx: 'healLowestAlly', amount: 55, range: 8 }], // TEST
    ui: { label: 'Fungal Renewal', showFloatingLabel: false },
  },
  {
    // bible v0.2.4 VW-08 Protective Bramble (Small-support/melee-only filter dropped:
    // applies to all nearby allies — deviation; aura-reapplication pattern)
    id: 'ab-vw-protective-bramble',
    name: 'Protective Bramble',
    trigger: { when: 'timer', period: 5 },
    internalCooldown: 5,
    telegraphSeconds: 0.2,
    targetSelector: 'self',
    effects: [{ fx: 'auraBuff', radius: 7, stat: 'damageTaken', mult: 0.85, duration: 6 }], // bible: 15% less
    ui: { label: 'Protective Bramble', showFloatingLabel: false },
  },
  {
    // bible v0.2.4 VW-08 Thornburst (absorbed-damage threshold approximated as a
    // health threshold; adjacent slow dropped — deviation)
    id: 'ab-vw-thornburst',
    name: 'Thornburst',
    trigger: { when: 'selfBelowHealth', fraction: 0.6, oncePerLife: false },
    internalCooldown: 20,
    telegraphSeconds: 0.4,
    targetSelector: 'self',
    effects: [{ fx: 'areaDamage', amount: 80, radius: 3 }], // TEST
    ui: { label: 'Thornburst', showFloatingLabel: true },
  },
  {
    // bible v0.2.4 VW-12 Mark Prey (support/caster/boss filter approximated as a
    // wounded-enemy trigger; pack speed/damage folded into the mark's damage-taken
    // debuff and the squad's fast base stats — deviation)
    id: 'ab-vw-mark-prey',
    name: 'Mark Prey',
    trigger: { when: 'enemyBelowHealth', fraction: 0.65 },
    internalCooldown: 18,
    telegraphSeconds: 0.3,
    targetSelector: 'currentTarget',
    effects: [
      { fx: 'mark', markId: 'moonfang-prey', duration: 10 },
      { fx: 'statMod', stat: 'damageTaken', mult: 1.15, duration: 10 }, // TEST
    ],
    ui: { label: 'Mark Prey', showFloatingLabel: true },
  },
  {
    // bible v0.2.4 VW-12 Pack Shadow (anti-body-block phasing approximated as a blink
    // toward an isolated target — deviation)
    id: 'ab-vw-pack-shadow',
    name: 'Pack Shadow',
    trigger: { when: 'targetIsolated', range: 8 },
    internalCooldown: 22,
    telegraphSeconds: 0.2,
    targetSelector: 'self',
    effects: [{ fx: 'teleport', distance: 4, direction: 'toward-target' }], // TEST
    ui: { label: 'Pack Shadow', showFloatingLabel: true },
  },
  {
    // bible v0.2.4 VW-13 Bloomfire (delayed burst as a long telegraph; seed-object
    // ignition dropped — deviation)
    id: 'ab-vw-bloomfire',
    name: 'Bloomfire',
    trigger: { when: 'surroundedBy', count: 2, range: 7 },
    internalCooldown: 15,
    telegraphSeconds: 0.6,
    targetSelector: 'triggeringEntity',
    effects: [{ fx: 'areaDamage', amount: 110, radius: 3.5 }], // TEST
    ui: { label: 'Bloomfire', showFloatingLabel: true },
  },
  {
    // bible v0.2.4 VW-13 Nectar Shield (damage-absorb coat approximated as periodic
    // lowest-ally healing — deviation)
    id: 'ab-vw-nectar-shield',
    name: 'Nectar Shield',
    trigger: { when: 'timer', period: 9 },
    internalCooldown: 9,
    telegraphSeconds: 0.3,
    targetSelector: 'self',
    effects: [{ fx: 'healLowestAlly', amount: 90, range: 10 }], // TEST
    ui: { label: 'Nectar Shield', showFloatingLabel: false },
  },
  {
    // bible v0.2.4 VW-14 Rootwalk (living-territory tunnel approximated as a periodic
    // self move-speed surge — deviation)
    id: 'ab-vw-rootwalk',
    name: 'Rootwalk',
    trigger: { when: 'timer', period: 16 },
    internalCooldown: 16,
    telegraphSeconds: 0.4,
    targetSelector: 'self',
    effects: [{ fx: 'statMod', stat: 'moveSpeed', mult: 1.35, duration: 6 }], // TEST
    ui: { label: 'Rootwalk', showFloatingLabel: true },
  },
  {
    // bible v0.2.4 VW-14 Branch Slam (every-4th-attack sweep approximated as a
    // when-surrounded arc; Medium slow dropped — deviation)
    id: 'ab-vw-branch-slam',
    name: 'Branch Slam',
    trigger: { when: 'surroundedBy', count: 3, range: 4, sizeTags: ['small', 'medium'] },
    internalCooldown: 14,
    telegraphSeconds: 0.5,
    targetSelector: 'triggeringEntity',
    effects: [
      { fx: 'areaDamage', amount: 75, radius: 3 }, // TEST
      { fx: 'knockback', distance: 3 },
    ],
    ui: { label: 'Branch Slam', showFloatingLabel: true },
  },
  {
    // bible v0.2.4 VW-15 Regrow Heads (70%/40% dual threshold approximated as one
    // repeatable 60% threshold; fire-cancel window dropped — deviation)
    id: 'ab-vw-regrow-heads',
    name: 'Regrow Heads',
    trigger: { when: 'selfBelowHealth', fraction: 0.6, oncePerLife: false },
    internalCooldown: 25,
    telegraphSeconds: 0.5,
    targetSelector: 'self',
    effects: [{ fx: 'heal', amount: 340, overSeconds: 6 }], // TEST
    ui: { label: 'Regrow Heads', showFloatingLabel: true },
  },
  {
    // bible v0.2.4 VW-15 Acid Sap (multi-head stacking armor shred approximated as a
    // periodic damage-taken debuff on the current target — deviation)
    id: 'ab-vw-acid-sap',
    name: 'Acid Sap',
    trigger: { when: 'timer', period: 6 },
    internalCooldown: 6,
    telegraphSeconds: 0.3,
    targetSelector: 'currentTarget',
    effects: [{ fx: 'statMod', stat: 'damageTaken', mult: 1.15, duration: 6 }], // TEST
    ui: { label: 'Acid Sap', showFloatingLabel: false },
  },
  {
    // bible v0.2.4 VW-16 Solar Bloom
    id: 'ab-vw-solar-bloom',
    name: 'Solar Bloom',
    trigger: { when: 'timer', period: 18 }, // bible: every 18 seconds
    internalCooldown: 18,
    telegraphSeconds: 0.4,
    targetSelector: 'self',
    effects: [
      { fx: 'healLowestAlly', amount: 140, range: 10 }, // TEST
      { fx: 'auraBuff', radius: 10, stat: 'moveSpeed', mult: 1.15, duration: 4 }, // TEST
    ],
    ui: { label: 'Solar Bloom', showFloatingLabel: true },
  },
  {
    // bible v0.2.4 VW-16 Resurrection Seed (squad revival approximated as a temporary
    // Sproutling squad sprouting where an ally fell — deviation)
    id: 'ab-vw-resurrection-seed',
    name: 'Resurrection Seed',
    trigger: { when: 'allyDiedNearby', range: 10 },
    internalCooldown: 45,
    telegraphSeconds: 0.5,
    targetSelector: 'self',
    effects: [{ fx: 'spawnTemporary', unitId: 'u-vw-sproutling-swarm', count: 1, duration: 25 }], // TEST
    ui: { label: 'Resurrection Seed', showFloatingLabel: true },
  },
  {
    // bible v0.2.4 VW-17 Overgrowth Trail (vine-trail territory approximated as a
    // periodic heal + ally speed aura; enemy Small slow dropped — deviation)
    id: 'ab-vw-overgrowth-trail',
    name: 'Overgrowth Trail',
    trigger: { when: 'timer', period: 8 },
    internalCooldown: 8,
    telegraphSeconds: 0.3,
    targetSelector: 'self',
    effects: [
      { fx: 'healLowestAlly', amount: 80, range: 9 }, // TEST
      { fx: 'auraBuff', radius: 9, stat: 'moveSpeed', mult: 1.1, duration: 5 }, // TEST
    ],
    ui: { label: 'Overgrowth Trail', showFloatingLabel: false },
  },
  {
    // bible v0.2.4 VW-17 Crushing Roll (rolling charge approximated as a heavy
    // structure strike; infantry knock-aside dropped — deviation)
    id: 'ab-vw-crushing-roll',
    name: 'Crushing Roll',
    trigger: { when: 'attackingTargetTag', tag: 'building' },
    internalCooldown: 20,
    telegraphSeconds: 0.6,
    targetSelector: 'currentTarget',
    effects: [{ fx: 'structureStrike', damage: 380, repairHalfSeconds: 2 }], // TEST
    ui: { label: 'Crushing Roll', showFloatingLabel: true },
  },
  {
    // owner request (not in bible): beast giants charge to close distance — charge extension
    id: 'ab-vw-behemoth-charge',
    name: 'Thundering Rush',
    trigger: { when: 'enemyInRange', range: 18, minRange: 5 },
    internalCooldown: 30, // TEST
    telegraphSeconds: 0.6,
    targetSelector: 'triggeringEntity',
    effects: [
      { fx: 'charge', speedMult: 2.5, durationSeconds: 2.5, impactDamage: 120, impactSlowMult: 0.6, impactSlowSeconds: 2 }, // TEST
    ],
    ui: { label: 'Charge', showFloatingLabel: true },
  },
  {
    // owner request (not in bible): humanoid boss giants hoist and crush small squads
    id: 'ab-vw-titan-crush',
    name: 'Rootbound Grasp',
    trigger: { when: 'surroundedBy', count: 1, range: 6, sizeTags: ['small'] },
    internalCooldown: 25, // TEST
    telegraphSeconds: 0.8,
    targetSelector: 'self',
    effects: [{ fx: 'crushGrab', maxTargetSize: 'small', kills: 2, range: 6 }], // TEST
    ui: { label: 'Crush', showFloatingLabel: true },
  },
];

// ---------------------------------------------------------------- buildings

export const VW_BUILDINGS: BuildingDef[] = [
  {
    id: 'b-vw-heartroot',
    name: 'Heartroot Core',
    faction: 'verdant-wilds',
    kind: 'core',
    maxHealth: 3800, // bible 7,200 × owner's ~0.53 TTK factor
    armor: 'moderate',
    footprint: { w: 8, h: 8 },
    isMajorStructure: true,
    buildTime: 0,
    placement: 'core-territory',
    regenOutOfCombat: 0.0035, // Living Bark baseline (also driven by coreDefense config)
    // no ranged core weapon: Grasping Roots + Root Lash are the defense (coreDefense.ts)
    // Core Annex System (owner spec 2026-07-13): three permanent sockets, all
    // eventually buildable — the strategic choice is build ORDER. Replaces the
    // v0.2 Rootheart Chamber / Bloom Nursery / Titan Roost track (README Deviations).
    upgrades: [
      {
        id: 'up-vw-rootward-nursery',
        name: 'Rootward Nursery',
        icon: '🌺',
        socket: 'top',
        cost: { power: 240, essence: 35 }, // spec
        buildTime: 15, // owner request 2026-07-13
        rulesText:
          'Grows a Rootwarden Treant every 75 s (max 2 alive). E: Take Root turns one into a stationary guardian that shields the economy.',
        spawner: { unitId: 'u-vw-rootwarden', period: 75, maxAlive: 2 }, // spec
      },
      {
        id: 'up-vw-deeproot-sanctuary',
        name: 'Deeproot Sanctuary',
        icon: '🌿',
        socket: 'left',
        cost: { power: 210, essence: 30 }, // spec
        buildTime: 15, // owner request 2026-07-13
        rulesText:
          '+4 Workforce, +6 shelter. Workers carry 20% more and retreat 15% faster. Deep Recall (90 s): up to six retreating workers burrow into the Core shelter.',
        effects: [
          { kind: 'workforceBonus', amount: 4 }, // spec
          { kind: 'shelterBonus', amount: 6 }, // spec
          // spec scopes both to the living root network; approximated as global
          // worker buffs (no root-network territory model) — see README Deviations
          { kind: 'carryMult', mult: 1.2 },
          { kind: 'workerRetreatSpeedMult', mult: 1.15 },
        ],
        deepRecall: { cooldown: 90, maxWorkers: 6, channelSeconds: 2 }, // spec
      },
      {
        id: 'up-vw-seedcaravan-bower',
        name: 'Seedcaravan Bower',
        icon: '🌰',
        socket: 'bottom',
        cost: { power: 270, essence: 50 }, // spec
        buildTime: 15, // owner request 2026-07-13
        rulesText:
          'Grows a Seedback Carrier every 110 s (one undeployed at a time). E: Plant Outpost grows a free Bramblehold that drops off resources and fires thorns.',
        spawner: { unitId: 'u-vw-seedback', period: 110, maxAlive: 1 }, // spec
      },
    ],
    ui: { color: 0x5f9e58, label: 'Heartroot' },
  },
  {
    // Core Annex System: free forward outpost grown from a planted Seedback.
    // Root-network extension approximated by its aura radius (README Deviations).
    id: 'b-vw-bramblehold',
    name: 'Bramblehold Outpost',
    faction: 'verdant-wilds',
    kind: 'defense',
    maxHealth: 1100, // TEST
    armor: 'light',
    footprint: { w: 4, h: 4 },
    isMajorStructure: false,
    buildTime: 50, // spec: the seed grows over 50 s
    placement: 'both',
    selfBuilding: true, // spec: free deployed buildings need no workers
    dropOff: ['power', 'essence'], // spec: Power and Essence drop-off
    passive: { kind: 'workerHealAura', radius: 12, perSecond: 2 }, // spec: minor worker healing // TEST
    summonAnchor: { maxAscension: 1, radius: 14 }, // spec: Ascension I Verdant summons nearby
    regenOutOfCombat: 0.002,
    attack: {
      damage: 16, // spec: weak thorns at raiders // TEST
      period: 1.3,
      range: 12,
      projectileSpeed: 22,
      targetPriority: ['worker', 'squad', 'large'],
    },
    ui: { color: 0x7aa05a, label: 'Bramblehold' },
  },
  {
    id: 'b-vw-site-tower',
    name: 'Warden Bough',
    faction: 'verdant-wilds',
    kind: 'defense',
    maxHealth: 1500,
    armor: 'moderate',
    footprint: { w: 3, h: 3 },
    isMajorStructure: false,
    buildTime: 0,
    placement: 'site-radius',
    producesWorkers: true,
    isSiteTower: true,
    regenOutOfCombat: 0.002,
    signatureText: 'Guardian Vines root the first enemy entering the site every 25 s.',
    traits: [{ kind: 'periodicControl', radius: 13, cooldown: 25, effect: 'root', duration: 2.5 }],
    attack: {
      damage: 30,
      period: 1.0,
      range: 15,
      projectileSpeed: 24,
      targetPriority: ['squad', 'worker', 'large', 'giant'],
    },
    attackRequiresUpgrade: true,
    upgrade: {
      id: 'up-vw-thorn-launcher',
      name: 'Thorn Launcher',
      cost: { power: 150, essence: 0 },
      rulesText: 'Grows a thorn launcher that automatically fires at nearby enemies.',
    },
    ui: { color: 0x6fae62, label: 'Warden Bough' },
  },
  {
    id: 'b-vw-briarwatch-tower',
    name: 'Briarwatch Tower',
    faction: 'verdant-wilds',
    kind: 'defense',
    maxHealth: 1125, // TEST
    armor: 'light',
    footprint: { w: 3, h: 3 },
    isMajorStructure: true,
    buildTime: 31, // TEST
    placement: 'both',
    regenOutOfCombat: 0.002,
    signatureText: 'Seedshot: every fourth clustered thorn volley plants a slowing thorn patch at the target.',
    traits: [{ kind: 'seedVolley', every: 4, radius: 4, slowMult: 0.6, duration: 6 }],
    attack: {
      damage: 48, // TEST
      period: 1.25,
      range: 16,
      projectileSpeed: 22,
      damageVs: { small: 1.35, medium: 1.15 },
      targetPriority: ['squad', 'worker', 'large', 'giant'],
    },
    upgrade: {
      id: 'up-vw-canopy-crown',
      name: 'Canopy Crown',
      cost: { power: 175, essence: 35 }, // TEST
      rulesText: 'Intercepts a portion of ranged attacks aimed at nearby workers and economic buildings.',
    },
    ui: { color: 0x668f48, label: 'Briarwatch Tower' },
  },
  {
    id: 'b-vw-castle-wall',
    name: 'Rootwall',
    faction: 'verdant-wilds',
    kind: 'wall',
    maxHealth: 1400,
    armor: 'heavy',
    footprint: { w: 2, h: 12 },
    isMajorStructure: false,
    buildTime: 30, // TEST — owner: walls take a while to raise and are fragile mid-build
    placement: 'both',
    selfBuilding: true,
    mountSlots: 2,
    regenOutOfCombat: 0.002,
    signatureText: 'Entangling Breach binds enemies around a destroyed section unless fire suppresses the roots.',
    traits: [{ kind: 'breachRoot', radius: 5, duration: 3, disabledByFire: true }],
    ui: { color: 0x7a8a5a, label: 'Rootwall' },
  },
  {
    id: 'b-vw-ritual-standard',
    name: 'Awakening Totem',
    faction: 'verdant-wilds',
    kind: 'defense',
    maxHealth: 500,
    armor: 'none',
    footprint: { w: 2, h: 2 },
    isMajorStructure: false,
    buildTime: 0,
    placement: 'both',
    signatureText: 'Pollen Ward protects nearby troops and workers while a ritual channels.',
    ui: { color: 0x9dc06a, label: 'Awakening Totem' },
  },
  {
    id: 'b-vw-thorn-nursery',
    name: 'Thorn Nursery',
    faction: 'verdant-wilds',
    kind: 'production',
    maxHealth: 1300, // bible VW-05
    armor: 'light',
    footprint: { w: 5, h: 5 },
    isMajorStructure: true,
    buildTime: 38, // 32 s + 6 s growth phase folded in (deviation)
    placement: 'both',
    regenOutOfCombat: 0.003,
    signatureText: 'Protective Bloom slows intruders and heals workers when enemies first enter the Nursery area.',
    traits: [
      { kind: 'intrusionBloom', radius: 12, cooldown: 45, slowMult: 0.65, duration: 6, workerHeal: 90 },
      { kind: 'producedBuff', every: 3, stat: 'damageTaken', mult: 0.8, duration: 15, upgradeOnly: true },
    ],
    roster: {
      queueSize: 5,
      entries: [
        { unitId: 'u-vw-thorn-guard', cost: { power: 90, essence: 0, command: 3 }, trainTime: 23, ascension: 1 },
        { unitId: 'u-vw-root-mender', cost: { power: 135, essence: 20, command: 4 }, trainTime: 31, ascension: 2 },
        // bible v0.2.4 VW-18: focused plant-infantry pairing (function-based roster)
      ],
    },
    upgrade: {
      id: 'up-vw-elder-thorns',
      name: 'Elder Thorns',
      cost: { power: 155, essence: 30 },
      rulesText: 'Every third produced squad emerges with Barkskin, gaining a temporary shield-like damage reduction.',
    },
    ui: { color: 0x5f8e4a, label: 'Thorn Nursery' },
  },
  {
    id: 'b-vw-beast-hollow',
    name: 'Beast Hollow',
    faction: 'verdant-wilds',
    kind: 'production',
    maxHealth: 1450, // bible VW-06
    armor: 'moderate',
    footprint: { w: 5, h: 5 },
    isMajorStructure: true,
    buildTime: 46, // 40 s + growth phase
    placement: 'both',
    regenOutOfCombat: 0.003,
    signatureText: 'Scent of Expansion: after a site capture, the next Razorhorn Pack trains 25% faster.',
    traits: [
      { kind: 'captureTraining', trainMult: 1.25, duration: 90 },
      { kind: 'producedBuff', every: 1, stat: 'moveSpeed', mult: 1.2, duration: 8, upgradeOnly: true },
    ],
    roster: {
      queueSize: 5,
      entries: [
        { unitId: 'u-vw-razorhorn', cost: { power: 140, essence: 0, command: 4 }, trainTime: 29, ascension: 2 },
        // bible v0.2.4 VW-19: broad beast hub (function-based roster)
      ],
    },
    upgrade: {
      id: 'up-vw-primeval-blood',
      name: 'Primeval Blood',
      cost: { power: 210, essence: 55 },
      rulesText: 'Produced Razorhorns begin with an empowered opening charge and a burst of movement speed.',
    },
    ui: { color: 0x6a5a3a, label: 'Beast Hollow' },
  },
  {
    id: 'b-vw-living-grove',
    name: 'Living Grove',
    faction: 'verdant-wilds',
    kind: 'production',
    maxHealth: 1100, // bible VW-07
    armor: 'light',
    footprint: { w: 4, h: 4 },
    isMajorStructure: true,
    buildTime: 34,
    placement: 'both',
    dropOff: ['power', 'essence'],
    workforceBonus: 3,
    passive: { kind: 'depositPreservation', radius: 20, drainMult: 0.8 },
    regenOutOfCombat: 0.003,
    signatureText: 'Sap Surge pauses nearby gathering to rapidly restore connected living buildings and suppress fire penalties.',
    roster: {
      queueSize: 3,
      entries: [
        { unitId: 'u-vw-barkwright', cost: { power: 90, essence: 0 }, trainTime: 27, ascension: 2 },
      ],
    },
    upgrade: {
      id: 'up-vw-flourishing-heart',
      name: 'Flourishing Heart',
      cost: { power: 160, essence: 25 },
      rulesText: 'Deep Roots strengthen — barkwrights emerge hardier (+10% health).',
    },
    ui: { color: 0x7aa85a, label: 'Living Grove' },
  },
  {
    id: 'b-vw-skybloom-roost',
    name: 'Skybloom Roost',
    faction: 'verdant-wilds',
    kind: 'production',
    maxHealth: 1250, // bible VW-08
    armor: 'light',
    footprint: { w: 4, h: 4 },
    isMajorStructure: true,
    buildTime: 46,
    placement: 'both',
    regenOutOfCombat: 0.003,
    signatureText: 'Rising Current speeds nearby aerial and mounted support moving away from the Roost.',
    traits: [
      { kind: 'travelAura', radius: 18, moveMult: 1.15 },
      { kind: 'producedBuff', every: 1, stat: 'damage', mult: 1.2, duration: 8, upgradeOnly: true },
    ],
    roster: {
      queueSize: 3,
      entries: [
        { unitId: 'u-vw-canopy-riders', cost: { power: 175, essence: 45, command: 5 }, trainTime: 36, ascension: 3 },
        // bible v0.2.4 VW-20: one signature flying species (function-based roster)
      ],
    },
    upgrade: {
      id: 'up-vw-high-canopy-harness',
      name: 'High Canopy Harness',
      cost: { power: 220, essence: 75 },
      rulesText: 'Produced Canopy Riders begin with an empowered opening dive that knocks aside Small units.',
    },
    ui: { color: 0x8ab86a, label: 'Skybloom Roost' },
  },
  {
    // Seed of the Sleeping Grove eruption (temporary; thorn bolts + healing ground)
    id: 'b-vw-defense-grove',
    name: 'Sleeping Grove',
    faction: 'verdant-wilds',
    kind: 'defense',
    maxHealth: 900,
    armor: 'moderate',
    footprint: { w: 3, h: 3 },
    isMajorStructure: false,
    buildTime: 0,
    placement: 'both',
    passive: { kind: 'squadRecoveryAura', radius: 12, mult: 1.5 },
    signatureText: 'Final Awakening: when destroyed or expired, the Grove becomes a temporary Treant defender.',
    traits: [{ kind: 'finalAwakening', unitId: 'u-vw-ancient-treants', duration: 30 }],
    attack: {
      damage: 30,
      period: 1.0,
      range: 12,
      projectileSpeed: 24,
      targetPriority: ['large', 'giant', 'squad'],
    },
    ui: { color: 0x9dc06a, label: 'Sleeping Grove' },
  },
  {
    // Walking Forest sapling: Ascension-I summon anchor + gentle healing ground
    id: 'b-vw-sapling',
    name: 'Worldroot Sapling',
    faction: 'verdant-wilds',
    kind: 'defense',
    maxHealth: 250,
    armor: 'none',
    footprint: { w: 1, h: 1 },
    isMajorStructure: false,
    buildTime: 0,
    placement: 'both',
    summonAnchor: { maxAscension: 1, radius: 10 },
    passive: { kind: 'squadRecoveryAura', radius: 8, mult: 1.25 },
    signatureText: 'Rootway Arrival accelerates and heals arriving squads; a long-lived sapling matures its recovery field.',
    ui: { color: 0xa8d08a, label: 'Sapling' },
  },
];

// ---------------------------------------------------------------- spells / traps / rituals

export const VW_SPELLS: SpellDef[] = [
  {
    // bible VW-17
    id: 'sp-vw-regrowth',
    name: 'Regrowth',
    faction: 'verdant-wilds',
    targeting: { shape: 'entity', validTargets: 'friendly-any', range: 'global' },
    effects: [],
    entityHeal: { squadFraction: 0.28, giantFraction: 0.14, buildingAmount: 420, overSeconds: 8 },
  },
  {
    // bible VW-18 (healing-received and worker bonuses simplified out — deviation)
    id: 'sp-vw-bloom-surge',
    name: 'Bloom Surge',
    faction: 'verdant-wilds',
    targeting: { shape: 'circle', radius: 10, range: 'global' },
    zoneDuration: 14,
    effects: [],
    zoneBuffs: {
      appliesTo: 'friendly',
      entries: [
        { filter: 'all', stat: 'moveSpeed', mult: 1.15 },
        { filter: 'melee', stat: 'damageTaken', mult: 0.9 },
      ],
    },
  },
  {
    // bible VW-19
    id: 'sp-vw-wild-stampede',
    name: 'Wild Stampede',
    faction: 'verdant-wilds',
    targeting: { shape: 'lane', length: 34, width: 9, range: 'global' },
    effects: [],
    laneAssault: {
      telegraphSeconds: 3,
      damage: 160,
      knockback: 5,
      slowLargeSeconds: 8,
      speedBuffSeconds: 10,
    },
  },
];

export const VW_TRAPS: TrapDef[] = [
  {
    // bible VW-20
    id: 'tr-vw-circle-of-thorns',
    name: 'Circle of Thorns',
    faction: 'verdant-wilds',
    behavior: 'rootRing',
    pieceHealth: 350,
    perDeploymentPieces: 1,
    buildTime: 8, // TEST
    triggerRadius: 5,
    triggerSizeTags: ['small', 'medium'],
    effects: [
      { fx: 'root', duration: 3.5 },
      { fx: 'damage', amount: 60 },
    ],
    maxActiveFields: 2,
    minSquads: 2,
  },
  {
    // bible VW-21
    id: 'tr-vw-seed-grove',
    name: 'Seed of the Sleeping Grove',
    faction: 'verdant-wilds',
    behavior: 'seedGrove',
    pieceHealth: 500,
    perDeploymentPieces: 1,
    buildTime: 10, // TEST
    triggerRadius: 7,
    triggerSizeTags: ['small', 'medium', 'large', 'giant'],
    effects: [],
    maxActiveFields: 1,
    minSquads: 3,
    eruptBuildingId: 'b-vw-defense-grove',
    eruptSeconds: 25,
  },
];

export const VW_RITUALS: RitualDef[] = [
  {
    // bible VW-22
    id: 'rit-vw-great-awakening',
    name: 'The Great Awakening',
    faction: 'verdant-wilds',
    channelSeconds: 12,
    channelAt: 'both',
    standardHealth: 600, // TEST
    refundFraction: 0.35,
  },
];

// ---------------------------------------------------------------- cards (deck pool)

export const VW_CARDS: CardDef[] = [
  {
    id: 'card-vw-thorn-nursery',
    name: 'Thorn Nursery',
    faction: 'verdant-wilds',
    category: 'building',
    subjectId: 'b-vw-thorn-nursery',
    cost: { power: 165, essence: 0 },
    ascension: 1,
    summonLocations: ['core', 'controlled-site'],
    useModel: { kind: 'reusable', cooldown: 60 },
    maxActive: 2,
    hotkey: '',
    ui: {
      icon: '🌵',
      roleLine: 'Defensive infantry production',
      rulesText: 'Trains Thorn Guard and Root Menders only. Protective Bloom slows intruders and heals nearby workers.',
    },
  },
  {
    id: 'card-vw-beast-hollow',
    name: 'Beast Hollow',
    faction: 'verdant-wilds',
    category: 'building',
    subjectId: 'b-vw-beast-hollow',
    cost: { power: 230, essence: 25 },
    ascension: 2,
    summonLocations: ['core', 'controlled-site'],
    useModel: { kind: 'limited', maxUses: 2, cooldown: 70 },
    hotkey: '',
    ui: {
      icon: '🐗',
      roleLine: 'War-beast production',
      rulesText: 'Breeds only Razorhorn Packs. Capturing a site accelerates the next Pack; elite beasts remain direct cards.',
    },
  },
  {
    id: 'card-vw-living-grove',
    name: 'Living Grove',
    faction: 'verdant-wilds',
    category: 'building',
    subjectId: 'b-vw-living-grove',
    cost: { power: 175, essence: 0 },
    ascension: 1,
    summonLocations: ['core', 'controlled-site'],
    useModel: { kind: 'reusable', cooldown: 55 },
    maxActive: 3,
    hotkey: '',
    ui: {
      icon: '🌳',
      roleLine: 'Workforce & deposit life',
      rulesText:
        '+3 Workforce. Nearby deposits deplete 20% slower. Drop-off for Power and Essence. Trains Barkwrights (Asc II).',
    },
  },
  {
    id: 'card-vw-skybloom-roost',
    name: 'Skybloom Roost',
    faction: 'verdant-wilds',
    category: 'building',
    subjectId: 'b-vw-skybloom-roost',
    cost: { power: 285, essence: 80 },
    ascension: 3,
    summonLocations: ['core', 'controlled-site'],
    useModel: { kind: 'limited', maxUses: 2, cooldown: 80 },
    maxActive: 1,
    hotkey: '',
    ui: {
      icon: '🌺',
      roleLine: 'Mounting & boss support',
      rulesText: 'Trains Canopy Riders (Asc III) — elite archers that can mount Grovebacks and the Worldroot Titan.',
    },
  },
  {
    id: 'card-vw-razorhorn',
    name: 'Razorhorn Pack',
    faction: 'verdant-wilds',
    category: 'unit',
    subjectId: 'u-vw-razorhorn',
    cost: { power: 165, essence: 0, command: 4 },
    ascension: 2,
    summonLocations: ['core', 'controlled-site'],
    useModel: { kind: 'reusable', cooldown: 40 },
    hotkey: '',
    ui: {
      icon: '🐗',
      roleLine: 'Fast assault beasts',
      rulesText: 'Four armored boars. Splinter Charge scatters clustered enemies. Excellent raiders and trap-clearers.',
    },
  },
  {
    // bible v0.2.4 VW-04
    id: 'card-vw-sproutling-swarm',
    name: 'Sproutling Swarm',
    faction: 'verdant-wilds',
    category: 'unit',
    subjectId: 'u-vw-sproutling-swarm',
    cost: { power: 80, essence: 0, command: 2 },
    ascension: 1,
    summonLocations: ['core', 'controlled-site'],
    useModel: { kind: 'reusable', cooldown: 27 },
    hotkey: '',
    ui: {
      icon: '🌱',
      roleLine: 'Cheap capture swarm',
      rulesText: 'Eight sprouts flood capture circles. Bud and Split regrows losses; Pollen Pop softens blows when halved.',
    },
  },
  {
    // bible v0.2.4 VW-07
    id: 'card-vw-sporecallers',
    name: 'Sporecallers',
    faction: 'verdant-wilds',
    category: 'unit',
    subjectId: 'u-vw-sporecallers',
    cost: { power: 155, essence: 30, command: 4 },
    ascension: 2,
    summonLocations: ['core', 'controlled-site'],
    useModel: { kind: 'reusable', cooldown: 58 },
    maxActive: 2,
    hotkey: '',
    ui: {
      icon: '🍄',
      roleLine: 'Area denial & recovery',
      rulesText: 'Fungal mystics: clustered attackers are slowed and weakened, while allies nearby are steadily renewed.',
    },
  },
  {
    // bible v0.2.4 VW-08
    id: 'card-vw-briarback-wardens',
    name: 'Briarback Wardens',
    faction: 'verdant-wilds',
    category: 'unit',
    subjectId: 'u-vw-briarback-wardens',
    cost: { power: 170, essence: 20, command: 5 },
    ascension: 2,
    summonLocations: ['core', 'controlled-site'],
    useModel: { kind: 'reusable', cooldown: 60 },
    hotkey: '',
    ui: {
      icon: '🦔',
      roleLine: 'Protective tank',
      rulesText: 'Bramble shield-bearers: nearby allies take 15% less damage, and heavy punishment triggers a Thornburst.',
    },
  },
  {
    // bible v0.2.4 VW-12
    id: 'card-vw-moonfang-hunters',
    name: 'Moonfang Hunters',
    faction: 'verdant-wilds',
    category: 'unit',
    subjectId: 'u-vw-moonfang-hunters',
    cost: { power: 200, essence: 60, command: 5 },
    ascension: 3,
    summonLocations: ['core', 'controlled-site'],
    useModel: { kind: 'reusable', cooldown: 72 },
    hotkey: '',
    ui: {
      icon: '🐺',
      roleLine: 'Marked-target assassins',
      rulesText: 'Spectral wolves mark wounded prey to bring it down faster, and Pack Shadow blinks them onto isolated targets.',
    },
  },
  {
    // bible v0.2.4 VW-13
    id: 'card-vw-skybloom-drakes',
    name: 'Skybloom Drakes',
    faction: 'verdant-wilds',
    category: 'unit',
    subjectId: 'u-vw-skybloom-drakes',
    cost: { power: 240, essence: 75, command: 6 },
    ascension: 3,
    summonLocations: ['core', 'controlled-site'],
    useModel: { kind: 'limited', maxUses: 2, cooldown: 88 },
    hotkey: '',
    ui: {
      icon: '🐉',
      roleLine: 'Skimming area damage',
      rulesText: 'Flower-crested drakes: Bloomfire bursts over clustered enemies while nectar steadily shields the most wounded ally. 2 plays.',
    },
  },
  {
    // bible v0.2.4 VW-14
    id: 'card-vw-ancient-treants',
    name: 'Ancient Treants',
    faction: 'verdant-wilds',
    category: 'unit',
    subjectId: 'u-vw-ancient-treants',
    cost: { power: 235, essence: 70, command: 6 },
    ascension: 3,
    summonLocations: ['core', 'controlled-site'],
    useModel: { kind: 'reusable', cooldown: 82 },
    hotkey: '',
    ui: {
      icon: '🪵',
      roleLine: 'Slow control frontline',
      rulesText: 'Two elder tree spirits. Rootwalk surges their march; Branch Slam sweeps swarming infantry aside.',
    },
  },
  {
    // bible v0.2.4 VW-15
    id: 'card-vw-thornmaw-hydra',
    name: 'Thornmaw Hydra',
    faction: 'verdant-wilds',
    category: 'unit',
    subjectId: 'u-vw-thornmaw-hydra',
    cost: { power: 270, essence: 80, command: 8 },
    ascension: 3,
    summonLocations: ['core', 'controlled-site'],
    useModel: { kind: 'limited', maxUses: 2, cooldown: 94 },
    hotkey: '',
    ui: {
      icon: '🐍',
      roleLine: 'Sustained brawler',
      rulesText: 'A three-headed plant hydra that regrows heads as it survives and coats foes in armor-eating acid sap. 2 plays.',
    },
  },
  {
    // bible v0.2.4 VW-16
    id: 'card-vw-sunpetal-seraphs',
    name: 'Sunpetal Seraphs',
    faction: 'verdant-wilds',
    category: 'unit',
    subjectId: 'u-vw-sunpetal-seraphs',
    cost: { power: 300, essence: 120, command: 7 },
    ascension: 4,
    summonLocations: ['core', 'controlled-site'],
    useModel: { kind: 'limited', maxUses: 2, cooldown: 105 },
    hotkey: '',
    ui: {
      icon: '🌻',
      roleLine: 'Late-game protection',
      rulesText: 'Radiant spirits pulse healing every 18 s; fallen allies leave seeds that sprout Sproutling defenders. 2 plays.',
    },
  },
  {
    // bible v0.2.4 VW-17
    id: 'card-vw-verdant-behemoth',
    name: 'Verdant Behemoth',
    faction: 'verdant-wilds',
    category: 'unit',
    subjectId: 'u-vw-verdant-behemoth',
    cost: { power: 330, essence: 130, command: 9 },
    ascension: 4,
    summonLocations: ['core', 'controlled-site'],
    useModel: { kind: 'limited', maxUses: 2, cooldown: 108 },
    hotkey: '',
    ui: {
      icon: '🦣',
      roleLine: 'Territory-making assault',
      rulesText: 'A rolling ecosystem: its trail sustains and speeds your army, and Crushing Roll caves in structures. 2 plays.',
    },
  },
  {
    id: 'card-vw-regrowth',
    name: 'Regrowth',
    faction: 'verdant-wilds',
    category: 'spell',
    subjectId: 'sp-vw-regrowth',
    cost: { power: 85, essence: 0 },
    ascension: 1,
    summonLocations: ['anywhere-friendly'],
    useModel: { kind: 'reusable', cooldown: 52 },
    hotkey: '',
    ui: {
      icon: '🌿',
      roleLine: 'Reusable sustain',
      rulesText: 'Target friendly squad: restore 28% health (Giants 14%). Target structure: restore 420 health over 8 s.',
    },
  },
  {
    id: 'card-vw-bloom-surge',
    name: 'Bloom Surge',
    faction: 'verdant-wilds',
    category: 'spell',
    subjectId: 'sp-vw-bloom-surge',
    cost: { power: 135, essence: 20 },
    ascension: 2,
    summonLocations: ['anywhere-friendly'],
    useModel: { kind: 'limited', maxUses: 3, cooldown: 68 },
    hotkey: '',
    ui: {
      icon: '🌸',
      roleLine: 'Restorative ground',
      rulesText: 'For 14 s in the area: allies move 15% faster and melee takes 10% less damage.',
    },
  },
  {
    id: 'card-vw-stampede',
    name: 'Wild Stampede',
    faction: 'verdant-wilds',
    category: 'spell',
    subjectId: 'sp-vw-wild-stampede',
    cost: { power: 230, essence: 170 },
    ascension: 4,
    summonLocations: ['anywhere-friendly'],
    useModel: { kind: 'once' },
    hotkey: '',
    ui: {
      icon: '🦬',
      roleLine: 'Ultimate assault corridor',
      rulesText:
        'Once per match. After 3 s, spectral beasts charge the lane: infantry scattered, Large slowed, traps swept, your beasts and Giants accelerate.',
    },
  },
  {
    id: 'card-vw-circle-of-thorns',
    name: 'Circle of Thorns',
    faction: 'verdant-wilds',
    category: 'trap',
    subjectId: 'tr-vw-circle-of-thorns',
    cost: { power: 110, essence: 0 },
    ascension: 2,
    summonLocations: ['core', 'controlled-site'],
    useModel: { kind: 'reusable', cooldown: 60 },
    maxActive: 2,
    hotkey: '',
    ui: {
      icon: '🌾',
      roleLine: 'Anti-swarm root trap',
      rulesText: 'Triggers when 2+ enemy squads enter: roots them for 3.5 s. Large units are slowed; Giants unaffected.',
    },
  },
  {
    id: 'card-vw-seed-grove',
    name: 'Seed of the Sleeping Grove',
    faction: 'verdant-wilds',
    category: 'trap',
    subjectId: 'tr-vw-seed-grove',
    cost: { power: 175, essence: 40 },
    ascension: 3,
    summonLocations: ['core', 'controlled-site'],
    useModel: { kind: 'limited', maxUses: 2, cooldown: 70 },
    maxActive: 1,
    hotkey: '',
    ui: {
      icon: '🫘',
      roleLine: 'Defensive transformation',
      rulesText:
        'A visible pod. When a Large enemy, boss, or 3+ squads approach, it erupts into a thorn-bolt grove that heals your squads for 25 s.',
    },
  },
  {
    id: 'card-vw-awakening',
    name: 'The Great Awakening',
    faction: 'verdant-wilds',
    category: 'ritual',
    subjectId: 'rit-vw-great-awakening',
    cost: { power: 245, essence: 115 },
    ascension: 3,
    summonLocations: ['core', 'controlled-site'],
    useModel: { kind: 'once' },
    hotkey: '',
    ui: {
      icon: '🕯',
      roleLine: 'Battlefield infrastructure ritual',
      rulesText:
        'Channel 12 s. For 32 s: living structures regenerate even under light fire and site defenders gain 15% attack speed.',
    },
  },
  {
    id: 'card-vw-thornback',
    name: 'Thornback Charger',
    faction: 'verdant-wilds',
    category: 'boss',
    subjectId: 'boss-vw-thornback',
    cost: { power: 250, essence: 40, command: 7 },
    ascension: 2,
    summonLocations: ['core', 'controlled-site'],
    useModel: { kind: 'once' },
    hotkey: '',
    ui: {
      icon: '🐃',
      roleLine: 'Early pressure boss',
      rulesText: 'Once per match. A fast armored beast whose Thornquake Charge shakes buildings and everything beside them.',
    },
  },
  {
    id: 'card-vw-ancient',
    name: 'Ancient Groveback',
    faction: 'verdant-wilds',
    category: 'boss',
    subjectId: 'boss-vw-ancient-groveback',
    cost: { power: 395, essence: 125, command: 12 },
    ascension: 3,
    summonLocations: ['core', 'controlled-site'],
    useModel: { kind: 'once' },
    hotkey: '',
    ui: {
      icon: '🐢',
      roleLine: 'Walking site anchor',
      rulesText:
        'Once per match. A living hill: Orchard of War heals your most wounded squad every 20 s; one Canopy Rider squad may mount it.',
    },
  },
  {
    id: 'card-vw-titan',
    name: 'Worldroot Titan',
    faction: 'verdant-wilds',
    category: 'boss',
    subjectId: 'boss-vw-worldroot-titan',
    cost: { power: 610, essence: 255, command: 20 },
    ascension: 4,
    summonLocations: ['core', 'controlled-site'],
    useModel: { kind: 'once' },
    hotkey: '',
    ui: {
      icon: '🌲',
      roleLine: 'Ascension IV mounted finisher',
      rulesText:
        'Once per match. 60 s announced growth. Rootbreaker Fists crush structures; Walking Forest plants summon-anchor saplings; mounts 2 squads.',
    },
  },
  {
    id: 'card-vw-vine-archers',
    name: 'Vine Archers',
    faction: 'verdant-wilds',
    category: 'unit',
    subjectId: 'u-vw-vine-archers',
    cost: { power: 105, essence: 0, command: 3 }, // TEST
    ascension: 1,
    summonLocations: ['core', 'controlled-site'],
    useModel: { kind: 'reusable', cooldown: 36 }, // TEST
    hotkey: '',
    ui: { icon: '🏹', roleLine: 'Direct ranged summon', rulesText: 'Flexible ranged pressure outside the production queue.' },
  },
  {
    id: 'card-vw-canopy-stalkers',
    name: 'Canopy Stalkers',
    faction: 'verdant-wilds',
    category: 'unit',
    subjectId: 'u-vw-canopy-stalkers',
    cost: { power: 150, essence: 15, command: 4 }, // TEST
    ascension: 2,
    summonLocations: ['core', 'controlled-site'],
    useModel: { kind: 'reusable', cooldown: 56 }, // TEST
    hotkey: '',
    ui: { icon: '🦎', roleLine: 'Direct ambush-beast summon', rulesText: 'No longer bred by a normal queue. A direct card for mobile ranged harassment.' },
  },
  {
    id: 'card-vw-groveback',
    name: 'Groveback Beast',
    faction: 'verdant-wilds',
    category: 'unit',
    subjectId: 'u-vw-groveback',
    cost: { power: 235, essence: 65, command: 7 }, // TEST
    ascension: 3,
    summonLocations: ['core', 'controlled-site'],
    useModel: { kind: 'limited', maxUses: 2, cooldown: 88 }, // TEST
    hotkey: '',
    ui: { icon: '🐢', roleLine: 'Living transport', rulesText: 'A durable beast platform for carrying Verdant ranged support.' },
  },
  {
    id: 'card-vw-briarwatch-tower',
    name: 'Briarwatch Tower',
    faction: 'verdant-wilds',
    category: 'building',
    subjectId: 'b-vw-briarwatch-tower',
    cost: { power: 195, essence: 25 }, // TEST
    ascension: 2,
    summonLocations: ['core', 'controlled-site'],
    useModel: { kind: 'reusable', cooldown: 62 }, // TEST
    maxActive: 3,
    hotkey: '',
    ui: { icon: '🌵', roleLine: 'Dedicated anti-swarm tower', rulesText: 'Clustered thorn volleys punish infantry; every fourth volley plants a slowing thorn patch.' },
  },
];

export const VW_BOSS_EXTRAS: Record<string, BossExtras> = {
  'boss-vw-thornback': { summonSeconds: 15, announcement: 'local' },
  'boss-vw-ancient-groveback': {
    summonSeconds: 30, // owner request 2026-07-13
    announcement: 'global',
    escortClassBonuses: { supportRegenPerSecond: 14 }, // Root Mender escorts sustain it
  },
  'boss-vw-worldroot-titan': {
    summonSeconds: 60, // owner request 2026-07-13
    announcement: 'global',
  },
};

/** Bible §8.3 "The Walking Forest" test deck (slot order = hotkey order). */
export const VW_DEFAULT_DECK: string[] = [
  'card-vw-thorn-nursery',
  'card-vw-beast-hollow',
  'card-vw-living-grove',
  'card-vw-skybloom-roost',
  'card-vw-razorhorn',
  'card-vw-regrowth',
  'card-vw-bloom-surge',
  'card-vw-stampede',
  'card-vw-circle-of-thorns',
  'card-vw-seed-grove',
  'card-vw-awakening',
  'card-vw-thornback',
  'card-vw-ancient',
  'card-vw-titan',
];
